﻿import {isNullOrEmpty, translation} from "@2d-front-monorepo/core";
import {
	absolute,
	clampTo,
	collapseWhitespace,
	lowerCase,
	MvvmValidation,
	MvvmValidationMethod,
	regexReplace,
	removeNonAlphanumeric,
	removeNonNumeric,
	removeSpecialChars,
	removeWhitespace,
	roundTo,
	trim,
	upperCase
} from "@2d-front-monorepo/design-library";
import {FieldRules, FieldTransform, FieldValidation} from "../types/InstallationByHostData";
import {useMemo} from "react";

interface ValidationFunctionFactoryResult {
	method: MvvmValidationMethod<any>;
	maxLength?: number;
	minLength?: number;
	max?: number;
	min?: number;
	pattern?: string;
}

type TransformationDelegate = (value: any) => any;

function getErrorKey(entity: string, validation: FieldValidation, fieldName: string): string {
	return `validation__${entity}__${fieldName}--${validation.$type}`;
}

function validationFunctionFactory(validation: FieldValidation, errorKey: string): ValidationFunctionFactoryResult | undefined {
	switch (validation.$type) {
		case "min-length":
			return {
				method: function (value): string | undefined {
					if (isNullOrEmpty(value) || value.length >= validation.length) return undefined;
					return translation(errorKey, {value: value.length, length: validation.length});
				},
				minLength: validation.length
			}

		case "max-length":
			return {
				method: function (value): string | undefined {
					if (isNullOrEmpty(value) || value.length <= validation.length) return undefined;
					return translation(errorKey, {value: value.length, length: validation.length});
				},
				maxLength: validation.length
			}

		case "min":
			return {
				method: function (value): string | undefined {
					if (value == null || value >= validation.value) return undefined;
					return translation(errorKey, {value: value, min: validation.value});
				},
				min: validation.value
			}

		case "max":
			return {
				method: function (value): string | undefined {
					if (value == null || value <= validation.value) return undefined;
					return translation(errorKey, {value: value, max: validation.value});
				},
				max: validation.value
			}

		case "regex":
			const regex = new RegExp(validation.pattern, validation.flags || "");
			return {
				method: function (value): string | undefined {
					if (isNullOrEmpty(value) || value.match(regex)) return undefined;
					return translation(errorKey, {value: value, pattern: validation.pattern});
				},
				pattern: validation.pattern
			}

		default:
			return undefined;
	}
}

function noTransformation(): TransformationDelegate {
	return function (value) {
		return value;
	}
}

function transformationFactory(transformation: FieldTransform): TransformationDelegate {
	switch (transformation.$type) {
		case "trim":
			return trim;

		case "regex":
			return regexReplace(transformation.pattern, transformation.replacement, transformation.flags);

		case "case":
			switch (transformation.case) {
				case "upper":
					return upperCase;

				case "lower":
					return lowerCase;

				default:
					return noTransformation();
			}

		case "collapse-whitespace":
			return collapseWhitespace;

		case "remove-chars":
			switch (transformation.charClass) {
				case "non-alphanumeric":
					return removeNonAlphanumeric;

				case "non-numeric":
					return removeNonNumeric;

				case "whitespace":
					return removeWhitespace;

				case "special-chars":
					return removeSpecialChars;

				default:
					return noTransformation();
			}

		case "round":
			return roundTo(transformation.decimals, transformation.mode);

		case "absolute":
			return absolute;

		case "clamp":
			return clampTo(transformation.min, transformation.max);

		case "date-boundary":
			return noTransformation();

		case "date-offset":
			return noTransformation();
	}
}

/**
 * Stage assignment for each `FieldTransform.$type`. This is the single place that decides whether a
 * given transformation runs as the user types (`onChange`) or only after focus leaves the input (`onBlur`).
 *
 * Rule of thumb:
 * - `onChange` is for transforms that are safe to apply to every partial value the user produces
 *   (case folding, anything monotonic that can't strip in-progress input).
 * - `onBlur` is for transforms that would interfere with mid-edit input — e.g. trim removes a space
 *   the user is about to type after, regex/replace mid-typing rewrites strings unpredictably,
 *   round/clamp on a half-typed number would snap the value before the user finishes.
 *
 * The `switch` is exhaustive: adding a new `$type` to `FieldTransform` without a stage here will
 * fail to type-check at the trailing `return` (no value of `FieldTransform["$type"]` will reach it).
 */
function transformStage(transformation: FieldTransform): "onChange" | "onBlur" {
	switch (transformation.$type) {
		case "case":
			return "onChange"; // upper / lower — safe live
		case "trim":
			return "onBlur";   // strips spaces the user is still typing
		case "collapse-whitespace":
			return "onBlur";   // similar to trim
		case "regex":
			return "onBlur";   // arbitrary mid-typing rewrites
		case "remove-chars":
			return "onBlur";   // could delete chars the user just typed
		case "round":
			return "onBlur";   // snaps a half-typed number
		case "absolute":
			return "onBlur";   // flips sign while typing "-…"
		case "clamp":
			return "onBlur";   // snaps a half-typed number
		case "date-boundary":
			return "onBlur";   // boundary snap mid-edit
		case "date-offset":
			return "onBlur";   // offset shift mid-edit
	}
}

function composeTransformations<TValue>(transformations: FieldTransform[]): TransformationDelegate | undefined {
	if (transformations.length === 0) {
		return undefined;
	}

	if (transformations.length === 1) {
		return transformationFactory(transformations[0]);
	}

	const transformationFunctions: ((value: TValue) => TValue)[] = [];
	for (const transformation of transformations) {
		transformationFunctions.push(transformationFactory(transformation));
	}

	return function (value: TValue): TValue {
		let result = value;
		for (const fn of transformationFunctions) {
			result = fn(result);
		}

		return result;
	}
}

export interface StagedTransform<TValue> {
	onChange?: (value: TValue) => TValue;
	onBlur?: (value: TValue) => TValue;
}

function createTransformations<TValue>(transformations: FieldTransform[] | undefined): StagedTransform<TValue> | undefined {
	if (transformations == null || transformations.length === 0) {
		return undefined;
	}

	const onChangeList: FieldTransform[] = [];
	const onBlurList: FieldTransform[] = [];
	for (const transformation of transformations) {
		if (transformStage(transformation) === "onChange") {
			onChangeList.push(transformation);
		} else {
			onBlurList.push(transformation);
		}
	}

	const onChange = composeTransformations<TValue>(onChangeList);
	const onBlur = composeTransformations<TValue>(onBlurList);
	if (onChange == null && onBlur == null) {
		return undefined;
	}

	const result: StagedTransform<TValue> = {};
	if (onChange != null) result.onChange = onChange;
	if (onBlur != null) result.onBlur = onBlur;
	return result;
}

export interface FieldValidationResult<TValue> {
	validation?: MvvmValidation<TValue>;
	transform?: StagedTransform<TValue>;
	maxLength?: number;
	minLength?: number;
	max?: number;
	min?: number;
	pattern?: string;
}

export function useFieldValidation<T extends string | number | symbol, TValue>(fields: FieldRules<T>, field: T, entity: string, fieldName: string, customValidation?: MvvmValidationMethod<TValue>): FieldValidationResult<TValue> {
	return useMemo(function () {
		const fieldConfig = fields.validations[field];
		const transforms = fields.transforms?.[field];
		const result: FieldValidationResult<TValue> = {};

		const transform = createTransformations<TValue>(transforms);
		if (transform != null) {
			result.transform = transform;
		}

		const mvvmValidation: MvvmValidation<TValue> = {};

		if (fieldConfig != null) {
			const validationFunctions: MvvmValidationMethod<TValue>[] = [];

			for (const validation of fieldConfig) {
				if (validation.$type === "none") {
					continue;
				}

				const errorKey = getErrorKey(entity, validation, fieldName);
				if (validation.$type === "required") {
					mvvmValidation.required = translation(errorKey);

				} else {
					const vf = validationFunctionFactory(validation, errorKey);
					if (vf == null) {
						continue;
					}

					if (vf.minLength != null) result.minLength = vf.minLength;
					if (vf.maxLength != null) result.maxLength = vf.maxLength;
					if (vf.min != null) result.min = vf.min;
					if (vf.max != null) result.max = vf.max;
					if (vf.pattern != null) result.pattern = vf.pattern;

					validationFunctions.push(vf.method);
				}
			}

			if (customValidation != null) {
				validationFunctions.push(customValidation);
			}

			if (validationFunctions.length === 1) {
				mvvmValidation.validate = validationFunctions[0];

			} else if (validationFunctions.length > 1) {
				mvvmValidation.validate = function (value: TValue | undefined): string | undefined {
					for (const fn of validationFunctions) {
						const result = fn(value);
						if (result != null) return result as string;
					}
					return undefined;
				};
			}
		} else if (customValidation != null) {
			mvvmValidation.validate = customValidation;
		}

		result.validation = Object.keys(mvvmValidation).length ? mvvmValidation : undefined;

		return result;

	}, [fields, field, entity, fieldName, customValidation]);
}
